import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    UserPlus,
    Copy,
    RotateCw,
    PowerOff,
    Power,
    CheckCircle2,
    Search,
    X,
    StickyNote,
    Mail,
    Save,
    Users,
    Sparkles,
    ShieldCheck,
    Upload,
    FolderOpen,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import CsvImportDialog from "@/components/admin/CsvImportDialog";

function Badge({ tone = "default", children }) {
    const map = {
        success: "bg-green-100 text-green-700",
        warning: "bg-orange-100 text-brand-orange",
        muted: "bg-gray-100 text-gray-600",
    };
    return (
        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${map[tone] || map.muted}`}>
            {children}
        </span>
    );
}

function formatDateFR(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
        return iso;
    }
}

function CreateFormateurDialog({ open, onClose, onCreated }) {
    const [form, setForm] = useState({ email: "", first_name: "", last_name: "", phone: "" });
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    const reset = () => {
        setForm({ email: "", first_name: "", last_name: "", phone: "" });
        setResult(null);
        setSubmitting(false);
    };

    const close = () => {
        reset();
        onClose();
    };

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const { data } = await api.post("/admin/users/formateur", {
                email: form.email.trim().toLowerCase(),
                first_name: form.first_name.trim(),
                last_name: form.last_name.trim(),
                phone: form.phone.trim() || null,
            });
            setResult(data);
            onCreated(data.user);
            toast.success(`Formateur ${data.user.first_name} créé`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const copyPwd = async () => {
        if (!result?.temporary_password) return;
        try {
            await navigator.clipboard.writeText(result.temporary_password);
            toast.success("Mot de passe copié");
        } catch {
            toast.error("Copie impossible");
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-ink/60 p-4" data-testid="create-formateur-dialog">
            <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="relative w-full max-w-lg rounded-3xl bg-white p-8 shadow-premium-lg"
            >
                <button
                    onClick={close}
                    className="absolute right-5 top-5 text-brand-smoke hover:text-brand-ink"
                    data-testid="dialog-close"
                    aria-label="Fermer"
                >
                    <X className="h-5 w-5" />
                </button>

                {!result ? (
                    <>
                        <h3 className="font-display text-3xl">Créer un formateur</h3>
                        <p className="mt-2 text-sm text-brand-smoke">
                            Un mot de passe provisoire sera généré. Communiquez-le au formateur : il devra le changer à sa première connexion.
                        </p>
                        <form onSubmit={submit} className="mt-8 space-y-4">
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-wider">Email *</label>
                                <input
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                                    data-testid="create-formateur-email"
                                    className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-wider">Prénom *</label>
                                    <input
                                        required
                                        value={form.first_name}
                                        onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                                        data-testid="create-formateur-first-name"
                                        className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-wider">Nom *</label>
                                    <input
                                        required
                                        value={form.last_name}
                                        onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                                        data-testid="create-formateur-last-name"
                                        className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-wider">Téléphone</label>
                                <input
                                    value={form.phone}
                                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                                    data-testid="create-formateur-phone"
                                    className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                                />
                            </div>
                            <div className="flex items-center justify-end gap-3 pt-4">
                                <button type="button" onClick={close} className="rounded-full px-5 py-2.5 text-sm text-brand-ink hover:bg-brand-line/50">
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="create-formateur-submit"
                                    className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#d9772c] disabled:opacity-60"
                                >
                                    {submitting ? "Création…" : "Créer le formateur"}
                                </button>
                            </div>
                        </form>
                    </>
                ) : (
                    <div data-testid="create-formateur-success">
                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-700">
                            <CheckCircle2 className="h-7 w-7" />
                        </div>
                        <h3 className="mt-6 font-display text-3xl">Formateur créé avec succès</h3>
                        <p className="mt-2 text-sm text-brand-smoke">
                            Communiquez le mot de passe provisoire au formateur. Il devra le changer à sa première connexion.
                        </p>
                        <div className="mt-6 rounded-2xl border border-brand-orange/30 bg-brand-orange/5 p-6">
                            <div className="text-xs font-semibold uppercase tracking-wider text-brand-orange">
                                Mot de passe provisoire
                            </div>
                            <div className="mt-3 flex items-center justify-between gap-3">
                                <code
                                    data-testid="temporary-password"
                                    className="font-display text-2xl font-semibold text-brand-ink"
                                >
                                    {result.temporary_password}
                                </code>
                                <button
                                    onClick={copyPwd}
                                    data-testid="copy-temporary-password"
                                    className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-4 py-2 text-xs font-semibold text-white hover:bg-brand-coal"
                                >
                                    <Copy className="h-3.5 w-3.5" /> Copier
                                </button>
                            </div>
                            <div className="mt-4 text-xs text-brand-smoke">
                                <strong>Email :</strong> {result.user.email}
                            </div>
                        </div>
                        <div className="mt-6 flex items-center justify-end">
                            <button
                                onClick={close}
                                className="rounded-full bg-brand-ink px-6 py-2.5 text-sm font-semibold text-white hover:bg-brand-coal"
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                )}
            </motion.div>
        </div>
    );
}

function ResetPasswordDialog({ user, onClose }) {
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    const reset = async () => {
        setLoading(true);
        try {
            const { data } = await api.post(`/admin/users/${user.id}/reset-password`);
            setResult(data);
            toast.success("Mot de passe réinitialisé");
        } catch (err) {
            toast.error(formatApiError(err));
            onClose();
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        reset();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const copy = async () => {
        if (!result?.temporary_password) return;
        await navigator.clipboard.writeText(result.temporary_password);
        toast.success("Mot de passe copié");
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-ink/60 p-4" data-testid="reset-password-dialog">
            <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="relative w-full max-w-lg rounded-3xl bg-white p-8 shadow-premium-lg"
            >
                <button onClick={onClose} className="absolute right-5 top-5 text-brand-smoke hover:text-brand-ink">
                    <X className="h-5 w-5" />
                </button>
                {loading ? (
                    <div className="py-10 text-center text-brand-smoke">Réinitialisation…</div>
                ) : result ? (
                    <>
                        <h3 className="font-display text-3xl">Mot de passe réinitialisé</h3>
                        <p className="mt-2 text-sm text-brand-smoke">
                            Pour <strong>{user.first_name} {user.last_name}</strong> ({user.email}). Communiquez ce mot de passe provisoire.
                        </p>
                        <div className="mt-6 rounded-2xl border border-brand-orange/30 bg-brand-orange/5 p-6">
                            <code data-testid="reset-temporary-password" className="font-display text-2xl font-semibold text-brand-ink">
                                {result.temporary_password}
                            </code>
                            <button
                                onClick={copy}
                                className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-ink px-4 py-2 text-xs font-semibold text-white"
                            >
                                <Copy className="h-3.5 w-3.5" /> Copier
                            </button>
                        </div>
                        <div className="mt-6 flex items-center justify-end">
                            <button onClick={onClose} className="rounded-full bg-brand-ink px-6 py-2.5 text-sm font-semibold text-white">
                                Fermer
                            </button>
                        </div>
                    </>
                ) : null}
            </motion.div>
        </div>
    );
}

function AdminNotesDialog({ user, onClose, onSaved }) {
    const [notes, setNotes] = useState(user?.admin_notes || "");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/admin/users/${user.id}/admin-notes`, { admin_notes: notes });
            toast.success("Notes admin enregistrées");
            onSaved(user.id, notes);
            onClose();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-ink/60 p-4" data-testid="admin-notes-dialog">
            <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="relative w-full max-w-xl rounded-3xl bg-white p-8 shadow-premium-lg"
            >
                <button onClick={onClose} className="absolute right-5 top-5 text-brand-smoke hover:text-brand-ink" data-testid="admin-notes-close">
                    <X className="h-5 w-5" />
                </button>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                    <StickyNote className="h-5 w-5" />
                </div>
                <h3 className="mt-6 font-display text-3xl">Notes admin</h3>
                <p className="mt-2 text-sm text-brand-smoke">
                    Pour <strong>{user.first_name} {user.last_name}</strong> · {user.email}
                </p>
                <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={10}
                    placeholder="Notes internes : statut de prise de contact, thèmes pressentis, villes, points de vigilance…"
                    data-testid="admin-notes-textarea"
                    className="mt-6 w-full resize-none rounded-2xl border border-brand-line bg-[#FAFAFA] p-4 text-sm leading-relaxed outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                />
                <div className="mt-2 text-right text-[11px] text-brand-smoke">{notes.length} / 4000</div>
                <div className="mt-4 flex items-center justify-end gap-3">
                    <button onClick={onClose} className="rounded-full px-5 py-2.5 text-sm text-brand-ink hover:bg-brand-line/50">
                        Annuler
                    </button>
                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid="admin-notes-save"
                        className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#d9772c] disabled:opacity-60"
                    >
                        <Save className="h-3.5 w-3.5" />
                        {saving ? "Enregistrement…" : "Enregistrer"}
                    </button>
                </div>
            </motion.div>
        </div>
    );
}

function SendWelcomeDialog({ user, onClose }) {
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    const send = async () => {
        setLoading(true);
        try {
            const { data } = await api.post(`/admin/users/${user.id}/send-welcome`);
            setResult(data);
            toast.success("Accès régénérés (email mocké)");
        } catch (err) {
            toast.error(formatApiError(err));
            onClose();
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        send();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const copy = async () => {
        if (!result?.temporary_password) return;
        await navigator.clipboard.writeText(result.temporary_password);
        toast.success("Mot de passe copié");
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-ink/60 p-4" data-testid="send-welcome-dialog">
            <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="relative w-full max-w-lg rounded-3xl bg-white p-8 shadow-premium-lg"
            >
                <button onClick={onClose} className="absolute right-5 top-5 text-brand-smoke hover:text-brand-ink">
                    <X className="h-5 w-5" />
                </button>
                {loading ? (
                    <div className="py-10 text-center text-brand-smoke">Régénération des accès…</div>
                ) : result ? (
                    <>
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-700">
                            <Mail className="h-5 w-5" />
                        </div>
                        <h3 className="mt-6 font-display text-3xl">Accès renvoyés</h3>
                        <p className="mt-2 text-sm text-brand-smoke">
                            Pour <strong>{user.first_name} {user.last_name}</strong> ({user.email}). L'envoi d'email est <strong>mocké</strong> : copiez le mot de passe ci-dessous et communiquez-le manuellement.
                        </p>
                        <div className="mt-6 rounded-2xl border border-brand-orange/30 bg-brand-orange/5 p-6">
                            <div className="text-xs font-semibold uppercase tracking-wider text-brand-orange">Mot de passe temporaire</div>
                            <div className="mt-3 flex items-center justify-between gap-3">
                                <code data-testid="welcome-temporary-password" className="font-display text-2xl font-semibold text-brand-ink">
                                    {result.temporary_password}
                                </code>
                                <button
                                    onClick={copy}
                                    data-testid="copy-welcome-password"
                                    className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-4 py-2 text-xs font-semibold text-white hover:bg-brand-coal"
                                >
                                    <Copy className="h-3.5 w-3.5" /> Copier
                                </button>
                            </div>
                        </div>
                        <div className="mt-6 flex items-center justify-end">
                            <button onClick={onClose} className="rounded-full bg-brand-ink px-6 py-2.5 text-sm font-semibold text-white">
                                Fermer
                            </button>
                        </div>
                    </>
                ) : null}
            </motion.div>
        </div>
    );
}

export default function AdminUsers() {
    const [users, setUsers] = useState([]);
    const [counts, setCounts] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ role: "", status: "", q: "", type: "" });
    const [createOpen, setCreateOpen] = useState(false);
    const [resetUser, setResetUser] = useState(null);
    const [notesUser, setNotesUser] = useState(null);
    const [welcomeUser, setWelcomeUser] = useState(null);
    const [importOpen, setImportOpen] = useState(false);

    const fetchCounts = async () => {
        try {
            const { data } = await api.get("/admin/users/counts");
            setCounts(data);
        } catch {
            /* silent */
        }
    };

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.role) params.role = filters.role;
            if (filters.status) params.status = filters.status;
            if (filters.type) params.type = filters.type;
            const { data } = await api.get("/admin/users", { params });
            const filtered = filters.q
                ? data.filter((u) =>
                      (`${u.first_name} ${u.last_name} ${u.email}`).toLowerCase().includes(filters.q.toLowerCase())
                  )
                : data;
            setUsers(filtered);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
        fetchCounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.role, filters.status, filters.type]);

    const toggleStatus = async (u) => {
        const next = u.status === "active" ? "disabled" : "active";
        try {
            await api.patch(`/admin/users/${u.id}/status`, { status: next });
            toast.success(next === "active" ? "Compte activé" : "Compte désactivé");
            fetchUsers();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div className="mx-auto max-w-7xl px-6 py-10" data-testid="page-admin-users">
            <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                <div>
                    <h1 className="font-display text-5xl font-normal tracking-tightest">Utilisateurs</h1>
                    <p className="mt-2 text-brand-smoke">Gestion des comptes (admin, formateurs, stagiaires).</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        onClick={() => setImportOpen(true)}
                        data-testid="admin-import-csv-btn"
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-line bg-white px-5 py-2.5 text-sm font-medium hover:border-brand-orange hover:text-brand-orange"
                    >
                        <Upload className="h-3.5 w-3.5" /> Importer CSV
                    </button>
                    <button
                        onClick={() => setCreateOpen(true)}
                        data-testid="admin-create-formateur-btn"
                        className="inline-flex items-center justify-center gap-2 self-start rounded-full bg-brand-orange px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-[#d9772c] md:self-auto"
                    >
                        <UserPlus className="h-4 w-4" />
                        Créer un formateur
                    </button>
                </div>
            </div>

            {/* Counters */}
            {counts && (
                <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="admin-users-counters">
                    <div className="rounded-2xl border border-brand-line bg-white p-5 shadow-premium">
                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-smoke">
                            <Sparkles className="h-3.5 w-3.5 text-brand-orange" /> Formateurs réels
                        </div>
                        <div className="mt-2 font-display text-3xl text-brand-ink" data-testid="counter-real-formateurs">{counts.real_formateurs}</div>
                    </div>
                    <div className="rounded-2xl border border-brand-line bg-white p-5 shadow-premium">
                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-smoke">
                            <Users className="h-3.5 w-3.5" /> Formateurs démo
                        </div>
                        <div className="mt-2 font-display text-3xl text-brand-ink" data-testid="counter-demo-formateurs">{counts.demo_formateurs}</div>
                    </div>
                    <div className="rounded-2xl border border-brand-line bg-white p-5 shadow-premium">
                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-smoke">
                            <Users className="h-3.5 w-3.5" /> Stagiaires
                        </div>
                        <div className="mt-2 font-display text-3xl text-brand-ink" data-testid="counter-stagiaires">{counts.real_stagiaires + counts.demo_stagiaires}</div>
                    </div>
                    <div className="rounded-2xl border border-brand-line bg-white p-5 shadow-premium">
                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-smoke">
                            <ShieldCheck className="h-3.5 w-3.5" /> Admins
                        </div>
                        <div className="mt-2 font-display text-3xl text-brand-ink" data-testid="counter-admins">{counts.admins}</div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="mt-8 flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[240px]">
                    <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-smoke" />
                    <input
                        type="text"
                        placeholder="Rechercher par nom ou email…"
                        value={filters.q}
                        onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                        onKeyDown={(e) => e.key === "Enter" && fetchUsers()}
                        data-testid="admin-users-search"
                        className="w-full rounded-full border border-brand-line bg-white py-2.5 pl-11 pr-4 text-sm outline-none focus:border-brand-orange"
                    />
                </div>
                <select
                    value={filters.type}
                    onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))}
                    data-testid="admin-filter-type"
                    className="rounded-full border border-brand-line bg-white px-5 py-2.5 text-sm outline-none focus:border-brand-orange"
                >
                    <option value="">Tous comptes</option>
                    <option value="real">Comptes réels</option>
                    <option value="demo">Comptes démo</option>
                </select>
                <select
                    value={filters.role}
                    onChange={(e) => setFilters((f) => ({ ...f, role: e.target.value }))}
                    data-testid="admin-filter-role"
                    className="rounded-full border border-brand-line bg-white px-5 py-2.5 text-sm outline-none focus:border-brand-orange"
                >
                    <option value="">Tous rôles</option>
                    <option value="admin">Admin</option>
                    <option value="formateur">Formateur</option>
                    <option value="stagiaire">Stagiaire</option>
                </select>
                <select
                    value={filters.status}
                    onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                    data-testid="admin-filter-status"
                    className="rounded-full border border-brand-line bg-white px-5 py-2.5 text-sm outline-none focus:border-brand-orange"
                >
                    <option value="">Tous statuts</option>
                    <option value="active">Actif</option>
                    <option value="pending_onboarding">En attente</option>
                    <option value="disabled">Désactivé</option>
                </select>
                <button
                    onClick={() => { fetchUsers(); fetchCounts(); }}
                    className="rounded-full border border-brand-line bg-white px-4 py-2.5 text-sm font-medium hover:border-brand-orange hover:text-brand-orange"
                    data-testid="admin-users-refresh"
                >
                    Actualiser
                </button>
            </div>

            {/* Table */}
            <div className="mt-8 overflow-hidden rounded-2xl border border-brand-line bg-white shadow-premium" data-testid="admin-users-table">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-brand-line bg-[#FAFAFA] text-left text-xs font-semibold uppercase tracking-wider text-brand-smoke">
                                <th className="px-6 py-4">Utilisateur</th>
                                <th className="px-6 py-4">Rôle</th>
                                <th className="px-6 py-4">Statut</th>
                                <th className="px-6 py-4">Onboarding</th>
                                <th className="px-6 py-4">Notes admin</th>
                                <th className="px-6 py-4">Créé le</th>
                                <th className="px-6 py-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-brand-line">
                            {loading ? (
                                <tr><td colSpan={7} className="px-6 py-10 text-center text-brand-smoke">Chargement…</td></tr>
                            ) : users.length === 0 ? (
                                <tr><td colSpan={7} className="px-6 py-10 text-center text-brand-smoke">Aucun utilisateur.</td></tr>
                            ) : (
                                users.map((u) => (
                                    <tr key={u.id} className="hover:bg-[#FAFAFA]" data-testid={`user-row-${u.email}`}>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-orange/10 font-display text-sm text-brand-orange">
                                                    {(u.first_name || "?")[0]}
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-medium text-brand-ink">{u.first_name} {u.last_name}</span>
                                                        {u.is_demo_account ? (
                                                            <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-600" data-testid={`badge-demo-${u.email}`}>Démo</span>
                                                        ) : (
                                                            <span className="inline-flex rounded-full bg-brand-orange/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-orange" data-testid={`badge-real-${u.email}`}>Réel</span>
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-brand-smoke">{u.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 capitalize text-brand-ink/80">{u.role}</td>
                                        <td className="px-6 py-4">
                                            <Badge tone={u.status === "active" ? "success" : u.status === "disabled" ? "muted" : "warning"}>
                                                {u.status === "active" ? "Actif" : u.status === "disabled" ? "Désactivé" : "En attente"}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4">
                                            {u.role === "formateur" ? (
                                                <Badge tone={u.onboarding_completed ? "success" : "warning"}>
                                                    {u.onboarding_completed ? "Complet" : "En cours"}
                                                </Badge>
                                            ) : <span className="text-brand-smoke">—</span>}
                                        </td>
                                        <td className="px-6 py-4 max-w-[260px]">
                                            {u.admin_notes ? (
                                                <div
                                                    className="line-clamp-2 cursor-pointer text-xs text-brand-ink/80 hover:text-brand-orange"
                                                    title={u.admin_notes}
                                                    onClick={() => setNotesUser(u)}
                                                    data-testid={`notes-preview-${u.email}`}
                                                >
                                                    {u.admin_notes}
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => setNotesUser(u)}
                                                    className="text-xs text-brand-smoke hover:text-brand-orange"
                                                    data-testid={`notes-add-${u.email}`}
                                                >
                                                    + Ajouter
                                                </button>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-brand-smoke">{formatDateFR(u.created_at)}</td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="inline-flex items-center gap-2">
                                                {u.role === "stagiaire" && (
                                                    <Link
                                                        to={`/admin/utilisateurs/${u.id}/dossier`}
                                                        title="Dossier complet"
                                                        data-testid={`user-dossier-${u.email}`}
                                                        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-line hover:border-brand-orange hover:text-brand-orange"
                                                    >
                                                        <FolderOpen className="h-3.5 w-3.5" />
                                                    </Link>
                                                )}
                                                {u.role !== "admin" && (
                                                    <button
                                                        onClick={() => setNotesUser(u)}
                                                        title="Notes admin"
                                                        data-testid={`user-notes-${u.email}`}
                                                        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-line hover:border-brand-orange hover:text-brand-orange"
                                                    >
                                                        <StickyNote className="h-3.5 w-3.5" />
                                                    </button>
                                                )}
                                                {u.role !== "admin" && (
                                                    <button
                                                        onClick={() => setWelcomeUser(u)}
                                                        title="Renvoyer accès par email"
                                                        data-testid={`user-welcome-${u.email}`}
                                                        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-line hover:border-brand-orange hover:text-brand-orange"
                                                    >
                                                        <Mail className="h-3.5 w-3.5" />
                                                    </button>
                                                )}
                                                {u.role !== "admin" && (
                                                    <button
                                                        onClick={() => setResetUser(u)}
                                                        title="Réinitialiser le mot de passe"
                                                        data-testid={`user-reset-${u.email}`}
                                                        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-line hover:border-brand-orange hover:text-brand-orange"
                                                    >
                                                        <RotateCw className="h-3.5 w-3.5" />
                                                    </button>
                                                )}
                                                {u.role !== "admin" && (
                                                    <button
                                                        onClick={() => toggleStatus(u)}
                                                        title={u.status === "active" ? "Désactiver" : "Activer"}
                                                        data-testid={`user-toggle-${u.email}`}
                                                        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-line hover:border-brand-orange hover:text-brand-orange"
                                                    >
                                                        {u.status === "active" ? <PowerOff className="h-3.5 w-3.5" /> : <Power className="h-3.5 w-3.5" />}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <CreateFormateurDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={() => { fetchUsers(); fetchCounts(); }}
            />
            {resetUser && (
                <ResetPasswordDialog user={resetUser} onClose={() => setResetUser(null)} />
            )}
            {notesUser && (
                <AdminNotesDialog
                    user={notesUser}
                    onClose={() => setNotesUser(null)}
                    onSaved={(uid, notes) => setUsers((prev) => prev.map((u) => (u.id === uid ? { ...u, admin_notes: notes } : u)))}
                />
            )}
            {welcomeUser && (
                <SendWelcomeDialog user={welcomeUser} onClose={() => setWelcomeUser(null)} />
            )}
            <CsvImportDialog
                kind="users"
                open={importOpen}
                onClose={() => setImportOpen(false)}
                onImported={() => { fetchUsers(); fetchCounts(); }}
            />
        </div>
    );
}
